import { Box } from "@mui/material";
import type { FC } from "react";
import { BADGE, TALLY_STRIP } from "@/theme/tokens";

/** One run of the strip: how many units ended up one way, in which colour. `id` keys the run and names it in the DOM. */
export type TallyRun = { id: string; count: number; color: string };

type Props = {
	/** In display order. Runs of 0 draw nothing. */
	runs: readonly TallyRun[];
	/** Already translated: what the strip says, for a screen reader ("5 opened: 2 filled, 1 lowered, 2 still open"). */
	ariaLabel: string;
	dataTestId?: string;
};

/**
 * A whole-number total split by outcome — slots opened, then filled / lowered / still open — as a full-width row of short
 * pills, one per unit, coloured run by run, so the arithmetic reads at a glance. Past `TALLY_STRIP.MAX_UNITS` units it
 * draws one bar per run, sized to its share. For a score on a small fixed scale use `SegmentedMeter`; for a value
 * against a target, `TargetGapMeter`. Presentational: `role="img"` with the caller's label.
 */
export const TallyStrip: FC<Props> = ({ runs, ariaLabel, dataTestId }) => {
	const shown = runs.filter((run) => run.count > 0);
	for (const run of runs) if (!Number.isInteger(run.count) || run.count < 0) throw new Error(`TallyStrip run ${run.id} has count ${run.count}`);
	const total = shown.reduce((sum, run) => sum + run.count, 0);
	const perUnit = total <= TALLY_STRIP.MAX_UNITS;
	const pills = perUnit
		? shown.flatMap((run) => Array.from({ length: run.count }, (_, index) => ({ key: `${run.id}-${index}`, run, grow: 1 })))
		: shown.map((run) => ({ key: run.id, run, grow: run.count }));
	return (
		<Box role="img" aria-label={ariaLabel} data-testid={dataTestId} data-per-unit={perUnit} sx={{ display: "flex", gap: `${TALLY_STRIP.GAP_PX}px` }}>
			{pills.map(({ key, run, grow }) => (
				<Box
					key={key}
					data-run={run.id}
					sx={{ flex: `${grow} 1 0`, minWidth: 0, height: TALLY_STRIP.HEIGHT_PX, borderRadius: BADGE.PILL_RADIUS_FULL, backgroundColor: run.color }}
				/>
			))}
		</Box>
	);
};
